// CSV export for the Campaigns list (client-side, no IO beyond the download
// helper). RFC 4180 quoting, UTF-8 BOM so Excel reads accents, CRLF rows.

import {
  type LoyaltyCampaign,
  type LoyaltyFlow,
  displayStatusForCampaign,
  displayStatusForFlow,
} from '../../../types/loyaltyMarketing'
import {
  STATUS_PILL_LABELS,
  campaignType,
  emailRates,
  flowListType,
} from './campaignDisplay'

export const EXPORT_COLUMNS: string[] = [
  'name',
  'type',
  'status',
  'segment',
  'audience_size',
  'scheduled_at',
  'sent_at',
  'sent',
  'delivered',
  'delivered_pct',
  'opens',
  'open_pct',
  'clicks',
  'click_pct',
  'unsubscribes',
  'unsubscribe_pct',
  'conversions',
  'revenue',
  'subject',
]

type SegmentLookup = Record<string, { name: string; member_count?: number }>

// Quote when the field holds a comma, quote or line break; double the quotes.
// A leading = + - @ (or tab/CR) is neutralised so spreadsheets don't run it.
// Plain numbers (incl. negatives, as number or numeric string) are left alone.
const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/
export const csvField = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return ''
  let text = String(value)
  if (
    typeof value === 'string' &&
    /^[=+\-@\t\r]/.test(text) &&
    !PLAIN_NUMBER.test(text)
  )
    text = `'${text}`
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

const pct = (value: number | null): string =>
  value === null ? '' : String(value)

const isCampaign = (
  row: LoyaltyCampaign | LoyaltyFlow,
): row is LoyaltyCampaign => 'subject' in row && 'audience' in row

const rowCells = (
  row: LoyaltyCampaign | LoyaltyFlow,
  segmentsById: SegmentLookup,
): Array<string | number | null | undefined> => {
  const stats = row.stats
  const rates = emailRates(stats)
  let type: string
  let status: string
  let segment = ''
  let audienceSize: number | string = ''
  let scheduledAt: string | undefined
  let sentAt: string | undefined
  if (isCampaign(row)) {
    type = campaignType(row)
    status = STATUS_PILL_LABELS[displayStatusForCampaign(row)]
    const audience = row.audience
    if (audience?.type === 'segment') {
      const ids = audience.segment_ids?.length
        ? audience.segment_ids
        : audience.segment_id
          ? [audience.segment_id]
          : []
      const found = ids.map((id) => segmentsById[id]).filter(Boolean)
      segment = found.map((s) => s.name).join('; ')
      if (found.length) {
        audienceSize = found.reduce((sum, s) => sum + (s.member_count ?? 0), 0)
      }
    } else if (!audience || audience.type === 'all') {
      segment = 'All Customers'
    }
    if (audienceSize === '' && stats?.recipients !== undefined)
      audienceSize = stats.recipients
    scheduledAt = row.scheduled_at
    sentAt = row.sent_at
  } else {
    type = flowListType(row)
    status = STATUS_PILL_LABELS[displayStatusForFlow(row)]
  }
  return [
    row.name,
    type,
    status,
    segment,
    audienceSize,
    scheduledAt,
    sentAt,
    stats?.sent ?? 0,
    rates.delivered.count,
    pct(rates.delivered.pct),
    rates.opens.count,
    pct(rates.opens.pct),
    rates.clicks.count,
    pct(rates.clicks.pct),
    rates.unsubscribes.count,
    pct(rates.unsubscribes.pct),
    rates.conversions.count,
    stats?.revenue ?? 0,
    (row as { subject?: string }).subject ?? '',
  ]
}

export const campaignsToCsv = (
  rows: Array<LoyaltyCampaign | LoyaltyFlow>,
  segmentsById: SegmentLookup,
): string => {
  const lines = [
    EXPORT_COLUMNS.join(','),
    ...rows.map((row) => rowCells(row, segmentsById).map(csvField).join(',')),
  ]
  return `﻿${lines.join('\r\n')}\r\n`
}

export const downloadCsv = (csv: string, filename?: string): void => {
  const name =
    filename ?? `campaigns-${new Date().toISOString().slice(0, 10)}.csv`
  const url = URL.createObjectURL(
    new Blob([csv], { type: 'text/csv;charset=utf-8' }),
  )
  const link = document.createElement('a')
  link.href = url
  link.download = name
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
